'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import BusinessTypeAddEdit from './BusinessTypeAddEdit';
import {
  useBusinessTypeList,
  deleteBusinessType,
  revalidateBusinessTypeList,
  fetchAllBusinessType
} from '../../../stores/businessTypeStore';

const BusinessTypePage = () => {
  const { t } = useLanguage();
  const notify = useNotifier();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('general.name', 'Name') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  // --- DEBOUNCE SEARCH INPUT ---
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = String(searchRaw ?? '').trim();
      setSearchValue((prev) => {
        if (prev !== next) {
          setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
        }
        return next;
      });
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw]);

  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading
  } = useBusinessTypeList(paginationModel.page, paginationModel.pageSize, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllBusinessType({
        searchKey: searchField,
        searchValue,
        total: totalRecords
      });
    } catch (err) {
      console.error('business type export all error', err);
      return [];
    }
  };

  const handleSuccess = () => {
    setAddEditOpen(false);
    setEditId(null);
    revalidateBusinessTypeList();
    setPaginationModel((p) => ({ ...p }));
  };

  const handleAdd = () => {
    setEditId(null);
    setAddEditOpen(true);
  };

  const handleEdit = (id) => {
    setEditId(id);
    setAddEditOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteBusinessType(itemToDelete.id);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      revalidateBusinessTypeList();
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error('Failed to delete business type:', err);
      const errorMessage =
        err?.response?.data?.message || err?.data?.message || err?.message || t('notify.deleteFailed', 'Failed to delete');
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('general.name', 'Name'),
        flex: 1,
        minWidth: 180
      },
      {
        field: 'description',
        headerName: t('general.description', 'Description'),
        flex: 1.5,
        minWidth: 220,
        valueGetter: (value, row) => row?.description || '-'
      },
      ...(currentUserRole === 'Administrator'
        ? [
            { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
            { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
            { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
            { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
          ]
        : []),
      {
        field: 'actions',
        headerName: t('general.actions', 'Actions'),
        width: 100,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <ActionMenu mode="inline" row={params.row} onEdit={handleEdit} onDelete={() => handleDeleteClick(params.row)} />
        )
      }
    ],
    [t, currentUserRole]
  );

  return (
    <Box>
      <DataTable
        title={t('businessType.title', 'Business Type')}
        columns={columns}
        rows={rows}
        loading={isLoading}
        pagination
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          if (field && field !== searchField) setSearchField(field);
          setSearchRaw(value ?? '');
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f || 'all');
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
        }}
        searchLabel={t('general.searchByName', 'Search by name')}
        onAddClick={handleAdd}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <BusinessTypeAddEdit
        open={addEditOpen}
        id={editId}
        onClose={() => {
          setAddEditOpen(false);
          setEditId(null);
        }}
        onSuccess={handleSuccess}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        loading={isDeleting}
        onClose={() => {
          if (isDeleting) return;
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={itemToDelete?.name || ''}
      />
    </Box>
  );
};

export default BusinessTypePage;
